import { Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { ScullyRoute, ScullyRoutesService } from '@scullyio/ng-lib'
import { BehaviorSubject, merge, Observable, of } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'

@Injectable({
  providedIn: 'any',
})
export class GuideRoutingService {
  linkUltimateGuidePaths$: Observable<ScullyRoute[]> = this.scully.available$.pipe(
    map(routeList => routeList.filter(this.isContentPath.bind(this))),
  )

  groups$ = this.linkUltimateGuidePaths$.pipe(map(routes => this.groupAndOrderRoute(routes).sort()))

  routes$ = this.groups$.pipe(map(groups => groups.reduce((acc, group) => [...acc, ...group.routes], [])))

  constructor(private scully: ScullyRoutesService, private router: Router) {}

  get prev$() {
    return this.getCurrent().pipe(
      withLatestFrom(this.routes$),
      map(([current, routes]) => {
        if (!current) {
          return null
        }
        const index = routes.findIndex(c => c.order === current.order && c.groupOrder === current.groupOrder)
        if (index < 0) {
          return null
        }

        if (index - 1 < 0) {
          return null
        }

        return routes[index - 1]
      }),
    )
  }

  get next$() {
    return this.getCurrent().pipe(
      withLatestFrom(this.routes$),
      map(([current, routes]) => {
        if (!current) {
          return null
        }
        const index = routes.findIndex(c => c.order === current.order && c.groupOrder === current.groupOrder)

        if (index === -1) {
          return null
        }

        if (index + 1 >= routes.length) {
          return null
        }

        return routes[index + 1]
      }),
    )
  }

  get breadcrumbs$() {
    let defaultPath = '/ultimateguide'
    if (this.router.url.startsWith('/en')) {
      defaultPath = `/en${defaultPath}`
    } else if (this.router.url.startsWith('/th')) {
      defaultPath = `/th${defaultPath}`
    }
    return this.getCurrent().pipe(
      withLatestFrom(this.groups$),
      map(([current, groups]) => {
        if (!current) {
          return []
        }
        const group = groups.find(c => c.order === current.groupOrder)
        return [
          { route: defaultPath, title: 'คู่มือการใช้งาน' },
          { route: `${defaultPath}/guide/${group.routes[0].slug}`, title: group.title },
          { route: current.route, title: current.title },
        ]
      }),
    )
  }

  get groupsWithActive$() {
    return this.getCurrent().pipe(
      withLatestFrom(this.groups$),
      map(([current, groups]) => {
        if (!current) {
          return groups
        }
        const groupsRemoveRef = JSON.parse(JSON.stringify(groups))
        const group: any = groupsRemoveRef.find(c => c.order === current.groupOrder)
        group.active = true

        const route = group.routes.find(c => c.order === current.order)
        route.active = true
        return groupsRemoveRef
      }),
    )
  }

  isContentPath(route: ScullyRoute) {
    let defaultPath = '/ultimateguide/guide/'
    if (this.router.url.startsWith('/en')) {
      defaultPath = `/en${defaultPath}`
    } else if (this.router.url.startsWith('/th')) {
      defaultPath = `/th${defaultPath}`
    }
    return route.route.startsWith(defaultPath) && route.sourceFile.endsWith('.md')
  }

  groupAndOrderRoute(routes) {
    const key = 'groupOrder'
    const group: { string: ScullyRoute[] } = routes.reduce(function(rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x)
      return rv
    }, {})

    return Object.keys(group)
      .map(groupOrder => ({
        order: group[groupOrder][0].groupOrder,
        title: group[groupOrder][0].group,
        route: group[groupOrder][0].route,
        routes: group[groupOrder].sort(s => s.order),
      }))
      .sort(s => s.order)
  }
  /**
   * returns an observable that returns the route information for the
   * route currently selected. subscribes to route-events to update when needed
   */
  getCurrent(): Observable<ScullyRoute> {
    if (!location) {
      /** probably not in a browser, no current location available */
      return of()
    }
    /** fire off at start, and when navigation is done. */
    return merge(of(new NavigationEnd(0, '', '')), this.router.events).pipe(
      filter(e => e instanceof NavigationEnd),
      switchMap(() => this.scully.available$),
      map(list => {
        const curLocation = decodeURIComponent(this.basePathOnly(encodeURI(location.pathname).trim()))
        return list.find(
          r =>
            curLocation === this.basePathOnly(encodeURI(r.route).trim()) ||
            (r.slugs &&
              Array.isArray(r.slugs) &&
              r.slugs.find(slug => curLocation.endsWith(this.basePathOnly(encodeURI(slug).trim())))),
        )
      }),
    )
  }

  basePathOnly(str: string): string {
    if (str.includes('#')) {
      str = str.split('#')[0]
    }
    if (str.includes('?')) {
      str = str.split('?')[0]
    }
    const cleanedUpVersion = str.endsWith('/') ? str.slice(0, -1) : str
    return cleanedUpVersion === '' ? '/' : cleanedUpVersion
  }
}
